import { createAction, createAsyncThunk, createReducer } from '@reduxjs/toolkit';
import { FeatureFlag } from '../types';
import { apiRequest } from '../utilities/api';

const FEATURE_FLAG_CACHE_LIMIT = process.env.REACT_APP_FEATURE_FLAG_CACHE_LIMIT
  ? parseInt(process.env.REACT_APP_FEATURE_FLAG_CACHE_LIMIT, 10) * 1000
  : 60 * 1000; // 60 seconds

export type FeatureFlagStore = {
  loading: boolean;
  featureFlags: FeatureFlag[];
  claimMissingCredits?: boolean;
  maintenanceMessage?: string;
  lastFetchedAt?: number;
};

const initialState: FeatureFlagStore = {
  loading: false,
  featureFlags: [],
  claimMissingCredits: false,
  maintenanceMessage: undefined,
  lastFetchedAt: undefined,
};

const CLAIM_MISSING_CREDITS_FLAG = 'claimMissingCredits';
const SHOW_MAINTENANCE_MESSAGE_FLAG = 'showMaintenanceMessage';

export const fetchFeatureFlags = createAsyncThunk<FeatureFlag[], void, { rejectValue: string }>(
  '@FEATURES/FETCH_FLAGS',
  async (_, { dispatch, rejectWithValue, getState }) => {
    const state = getState() as { featureFlags: FeatureFlagStore };
    const { lastFetchedAt } = state.featureFlags;

    if (lastFetchedAt && Date.now() - lastFetchedAt < FEATURE_FLAG_CACHE_LIMIT) {
      return state.featureFlags.featureFlags;
    }

    try {
      const { data: featureFlags } = await apiRequest.get<FeatureFlag[]>('/v1/feature-flags/app/learnerPortal');

      const claimMissingCreditsFlag = featureFlags.find((f) => f.enabled && f.feature === CLAIM_MISSING_CREDITS_FLAG);
      dispatch(claimMissingCredits(claimMissingCreditsFlag?.enabled || false));

      const showMaintenanceMessageFlag = featureFlags.find((f) => f.enabled && f.feature === SHOW_MAINTENANCE_MESSAGE_FLAG);
      dispatch(setMaintenanceMessage(showMaintenanceMessageFlag?.message || ''));

      return featureFlags;
    } catch (error) {
      Rollbar.error(`Failed to fetch feature flags. ${error}`);
      return rejectWithValue((error as Error).message);
    }
  }
);

export const claimMissingCredits = createAction<boolean>('CLAIM_MISSING_CREDITS');
export const setMaintenanceMessage = createAction<string>('SET_MAINTENANCE_MESSAGE');

const featureFlags = createReducer(initialState, (builder) =>
  builder
    .addCase(fetchFeatureFlags.pending, (state) => ({
      ...state,
      claimMissingCredits: false,
    }))
    .addCase(fetchFeatureFlags.fulfilled, (state, { payload }) => ({
      ...state,
      featureFlags: payload,
      loading: false,
      lastFetchedAt: Date.now(),
    }))
    .addCase(fetchFeatureFlags.rejected, (state) => ({
      ...state,
      claimMissingCredits: false,
      maintenanceMessage: undefined,
      loading: false,
    }))
    .addCase(claimMissingCredits, (state, { payload }) => ({
      ...state,
      claimMissingCredits: payload,
    }))
    .addCase(setMaintenanceMessage, (state, { payload }) => ({
      ...state,
      maintenanceMessage: payload,
    }))
);

export default featureFlags;
